/**
 * Antic Bike configurator render map (Step 2 accessory overlays).
 *
 * PNGs live in Shopify Files. Update `cdnBase` or version constants here when assets move.
 * Each component maps a product handle (+ optional swatch label) to a render filename.
 */
(function (global) {
  'use strict';

  var CDN_BASE =
    'https://cdn.shopify.com/s/files/1/0789/2572/5946/files/';
  var VERSION = 'V2';
  var BASE_VERSION = 'V3';

  /**
   * Step 2 complementary accessory list order (top → bottom in the accessories panel).
   *
   * - Reorder handles here to change display order.
   * - Add handles for products you want to show even if Shopify complementary recs omit them.
   * - Handles not found in the store are skipped.
   * - Only handles listed here appear in Step 2 (complementary recs not listed are excluded).
   *
   * Keep in sync with `handle_order` in snippets/antic-accessory-order.liquid (server render).
   */
  var ACCESSORY_HANDLE_ORDER = [
    'back-fender',
    'front-fender',
    'tail-light-kit',
    'cup-holder',
    'kickstand',
    'gt-charger-plug',
    'antic-faceplate',
    'hyper-charger',
    'antic-bar-pad',
    'antic-frame-decals',
    'antic-seat',
    'antic-footpegs',
    'bar-clamp-top',
    'bar-clamp-bottom',
    'peak-design-out-front-bike-mount',
    'rear-pegs',
    'slider-pegs',
  ];

  /**
   * Accessories that auto-select the first available variant when the picker opens.
   * Keep in sync with `auto_preselect` in snippets/antic-accessory-order.liquid.
   */
  var ACCESSORY_AUTO_PRESELECT_HANDLES = ['gt-charger-plug'];

  /** Bike base layer — replaces the default gallery image in Step 2. */
  var BASE_LAYERS = [{ z: 0, file: 'AnticConfig_Base_' + BASE_VERSION + '.png' }];

  /**
   * Non-accessory bike option overlays (e.g. Performance Treaded tires).
   * Applied automatically from the main variant picker or tire accessory selection.
   */
  /**
   * Main bike model values (normalized) that include performance treaded tires.
   * "Antic Long Range +" normalizes to antic-long-range-
   */
  var PERFORMANCE_TREADED_BIKE_MODELS = ['antic-long-range-'];

  /** Known Shopify variant IDs for performance-treaded bike builds. */
  var PERFORMANCE_TREADED_BIKE_VARIANT_IDS = ['47965275226362'];

  var BIKE_OPTION_LAYERS = [
    {
      id: 'performance-treaded-tire',
      z: 5,
      file: 'AnticConfig_PerfTread_V1.png',
      tireHandles: ['gt-performance-tire'],
      bikeModelKeys: PERFORMANCE_TREADED_BIKE_MODELS,
      variantIds: PERFORMANCE_TREADED_BIKE_VARIANT_IDS,
    },
  ];

  function isBikeRenderModeActive() {
    return !!getBikeSection();
  }

  function isConfiguratorStepActive() {
    var shell = document.querySelector('.bike-pdp');
    return !!(shell && shell.classList.contains('step-two'));
  }

  function getBaseLayerUrls() {
    return BASE_LAYERS.map(function (base) {
      return { url: cdnUrl(base.file), z: base.z };
    });
  }

  var COLOR_TOKENS = {
    black: 'Black',
    blue: 'Blue',
    'ano-blue': 'Blue',
    red: 'Red',
    white: 'White',
    raw: 'Raw',
    'raw-cnc': 'Raw',
    'raw-aluminum': 'Raw',
    chrome: 'Chrome',
    'dark-olive': 'DarkOlive',
    darkolive: 'DarkOlive',
    fuchsia: 'Fuchsia',
    fuschia: 'Fuschia',
    spearmint: 'Spearmint',
    saffron: 'Saffron',
    'hot-blue': 'HotBlue',
    hotblue: 'HotBlue',
  };

  /**
   * Component definitions — handles match anticbikes.com product URLs.
   * @see https://anticbikes.com/products/{handle}
   *
   * Products without configurator PNGs yet (hyper-charger) are omitted.
   *
   * bar-clamp-top / bar-clamp-bottom — AnticConfig_BarClamp_{Color}_V3_{Top|Bottom}.png
   *
   * `defaultColor` — fallback when swatch color is not mapped yet; uses an existing
   * render from Shopify Files so overlays still appear during Step 2 testing.
   */
  var COMPONENTS = [
    {
      handles: ['bar-clamp-top'],
      prefix: 'BarClamp',
      position: 'Top',
      version: 'V3',
      z: 10,
      defaultColor: 'Red',
      colors: {
        black: 'Black',
        blue: 'Blue',
        'ano-blue': 'Blue',
        red: 'Red',
        raw: 'Raw',
      },
    },
    {
      handles: ['bar-clamp-bottom'],
      prefix: 'BarClamp',
      position: 'Bottom',
      version: 'V3',
      z: 9,
      defaultColor: 'Black',
      colors: {
        black: 'Black',
        blue: 'Blue',
        'ano-blue': 'Blue',
        red: 'Red',
        raw: 'Raw',
      },
    },
    {
      handles: ['antic-bar-pad'],
      prefix: 'BarPad',
      z: 11,
      layers: ['-1', '-2'],
      defaultColor: 'White',
      colors: {
        black: 'STD_Black',
        'murdered-out': 'STD_Black',
        murderedout: 'STD_Black',
        camo: 'DarkOlive',
        'dark-olive': 'DarkOlive',
        darkolive: 'DarkOlive',
        flames: 'Fuschia',
        fuchsia: 'Fuschia',
        fuschia: 'Fuschia',
        'antic-logo': 'HotBlue',
        'hot-blue': 'HotBlue',
        hotblue: 'HotBlue',
        'antic-racing': 'Spearmint',
        spearmint: 'Spearmint',
        'warped-checkerboard': 'White',
        white: 'White',
      },
    },
    {
      handles: ['peak-design-out-front-bike-mount', 'cell-phone-holder'],
      file: 'AnticConfig_CellPhoneHolder_V3_NoBarpad.png',
      /* Bar pad blocks the default mount position — swap to WithBarpad art when both are selected. */
      padFile: 'AnticConfig_CellPhoneHolder_V3_WithBarpad.png',
      z: 12,
    },
    {
      handles: ['gt-charger-plug'],
      prefix: 'ChargePlug',
      z: 13,
      defaultColor: 'White',
      colors: {
        black: 'Black',
        'dark-olive': 'DarkOlive',
        fuchsia: 'Fuchsia',
        fuschia: 'Fuchsia',
        'fluorescent-orange': 'FloOrange',
        'fluorescent-yellow': 'FloYellow',
        'gold-nugget': 'GoldenNugget',
        'hot-blue': 'HotBlue',
        hotblue: 'HotBlue',
        'jewel-gray': 'JewelGrey',
        'jewel-grey': 'JewelGrey',
        lime: 'Lime',
        mint: 'Mint',
        'mississippi-mud': 'MississippiMud',
        'navy-blue': 'Navy',
        navy: 'Navy',
        purple: 'Purple',
        red: 'Red',
        saffron: 'Saffron',
        spearmint: 'Spearmint',
        white: 'White',
      },
    },
    {
      handles: ['cup-holder'],
      file: 'AnticConfig_CupHolder_V3_SlightlyLIghter_V2.png',
      z: 14,
    },
    {
      handles: ['tail-light-kit'],
      file: 'AnticConfig_TailLIght_V2.png',
      z: 17,
    },
    {
      handles: ['antic-frame-decals', 'decal'],
      prefix: 'Decal',
      z: 15,
      defaultColor: 'Spearmint',
      colors: {
        chrome: 'Chrome',
        'dark-olive': 'Dark-Olive',
        fuchsia: 'Fuchsia',
        fuschia: 'Fuchsia',
        'hot-blue': 'HotBlue',
        hotblue: 'HotBlue',
        red: 'Red',
        saffron: 'Saffron',
        spearmint: 'Spearmint',
        white: 'White',
      },
    },
    {
      handles: ['antic-faceplate'],
      prefix: 'FacePlate',
      z: 16,
      defaultColor: 'White',
      colors: {
        'dark-olive': 'DarkOlive',
        fuchsia: 'Fuchsia',
        fuschia: 'Fuchsia',
        'hot-blue': 'HotBlue',
        hotblue: 'HotBlue',
        red: 'Red',
        saffron: 'Saffron',
        spearmint: 'Spearmint',
        white: 'White',
      },
    },
    {
      handles: ['back-fender'],
      prefix: 'Fender_Back',
      z: 17,
      defaultColor: 'White',
      colors: {
        black: 'Black',
        blue: 'HotBlue',
        'dark-olive': 'DarkOlive',
        fuchsia: 'Fuchsia',
        fuschia: 'Fuchsia',
        'hot-blue': 'HotBlue',
        hotblue: 'HotBlue',
        red: 'Red',
        saffron: 'Saffron',
        spearmint: 'Spearmint',
        white: 'White',
      },
    },
    {
      handles: ['front-fender'],
      prefix: 'Fender_Front',
      z: 18,
      defaultColor: 'White',
      colors: {
        black: 'Black',
        blue: 'HotBlue',
        'dark-olive': 'DarkOlive',
        fuchsia: 'Fuchsia',
        fuschia: 'Fuchsia',
        'hot-blue': 'HotBlue',
        hotblue: 'HotBlue',
        red: 'Red',
        saffron: 'Saffron',
        spearmint: 'Spearmint',
        white: 'White',
      },
    },
    {
      handles: ['antic-footpegs'],
      prefix: 'FootPeg',
      z: 19,
      defaultColor: 'Black',
      colors: {
        black: 'Black',
        blue: 'Blue',
        red: 'Red',
        raw: 'Raw',
      },
    },
    {
      handles: ['kickstand'],
      file: 'AnticConfig_Kickstand_' + VERSION + '.png',
      z: 20,
    },
    {
      handles: ['antic-seat'],
      prefix: 'Seat',
      z: 21,
      colors: {
        'thrill-seeker': 'ThrillSeeker',
        thrillseeker: 'ThrillSeeker',
        'antic-x-thrill-seekers': 'ThrillSeeker',
      },
      match: function (variant) {
        if (!variant) return null;
        for (var i = 1; i <= 3; i++) {
          var raw = variant['option' + i];
          if (!raw) continue;
          var key = normalizeKey(raw);
          if (
            key === 'thrill-seeker' ||
            key === 'thrillseeker' ||
            key.indexOf('thrill-seek') !== -1
          ) {
            return 'ThrillSeeker';
          }
        }
        return null;
      },
    },
    {
      handles: ['rear-pegs'],
      file: 'AnticConfig_StuntPeg_V2_V2.png',
      z: 21,
    },
    {
      handles: ['slider-pegs'],
      file: 'AnticConfig_SliderPegHack_2.png',
      z: 22,
    },
    {
      handles: ['stunt-peg'],
      file: 'AnticConfig_StuntPeg_V2.png',
      z: 22,
    },
    {
      handles: ['surf-rack'],
      file: 'AnticConfig_SurfRack_wBoard_' + VERSION + '.png',
      z: 1,
    },
  ];

  var handleIndex = {};
  COMPONENTS.forEach(function (component) {
    component.handles.forEach(function (handle) {
      handleIndex[normalizeKey(handle)] = component;
    });
  });

  /**
   * Full render color lists for accessories whose Shopify color variants are incomplete.
   * Pipe-separated in snippets/antic-accessory-render-colorways.liquid — keep in sync.
   */
  var RENDER_COLORWAYS = {
    'antic-bar-pad': [
      'Murdered Out',
      'Antic Racing',
      'Flames',
      'Warped Checkerboard',
      'Camo',
      'Antic Logo',
    ],
    'back-fender': ['Black', 'Dark Olive', 'Fuchsia', 'Hot Blue', 'Red', 'Saffron', 'Spearmint', 'White'],
    'front-fender': ['Black', 'Dark Olive', 'Fuchsia', 'Hot Blue', 'Red', 'Saffron', 'Spearmint', 'White'],
    'bar-clamp-top': ['Black', 'Blue', 'Raw', 'Red'],
    'bar-clamp-bottom': ['Black', 'Blue', 'Raw', 'Red'],
    'antic-faceplate': ['Black', 'Dark Olive', 'Fuchsia', 'Hot Blue', 'Red', 'Saffron', 'Spearmint', 'White'],
    'antic-frame-decals': ['Spearmint', 'Chrome', 'Dark Olive', 'Red', 'Hot Blue', 'Saffron', 'Fuchsia', 'White'],
    'antic-footpegs': ['Black', 'Blue', 'Raw', 'Red'],
  };

  function getRenderColorways(productHandle) {
    return RENDER_COLORWAYS[normalizeKey(productHandle)] || null;
  }

  function normalizeKey(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[\s_]+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .trim();
  }

  function cdnUrl(filename) {
    return CDN_BASE + filename;
  }

  function isPerformanceTreadedLabel(value) {
    var key = normalizeKey(value);
    return key === 'performance-treaded' || key.indexOf('performance-treaded') !== -1;
  }

  function isPerformanceTreadedBikeModel(value) {
    var key = normalizeKey(value);
    return PERFORMANCE_TREADED_BIKE_MODELS.indexOf(key) !== -1;
  }

  function isPerformanceTreadedBikeVariantId(variantId) {
    if (variantId == null || variantId === '') return false;
    return PERFORMANCE_TREADED_BIKE_VARIANT_IDS.indexOf(String(variantId)) !== -1;
  }

  function getSelectedMainBikeVariant(bikeSection) {
    if (!bikeSection) return null;

    var picker = bikeSection.querySelector('variant-picker');
    if (picker) {
      var jsonEl = picker.querySelector('script[type="application/json"]');
      if (jsonEl && jsonEl.textContent) {
        try {
          return JSON.parse(jsonEl.textContent);
        } catch (e) {
          /* fall through */
        }
      }
    }

    var form =
      bikeSection.querySelector('[data-product-form] form') ||
      bikeSection.querySelector('product-form form') ||
      bikeSection.querySelector('form[action*="/cart/add"]');
    var variantInput = form && form.querySelector('input[name="id"]');
    if (variantInput && variantInput.value) {
      return { id: variantInput.value };
    }

    return null;
  }

  function variantHasPerformanceTreadedTires(variant) {
    if (!variant) return false;

    if (isPerformanceTreadedBikeVariantId(variant.id)) return true;

    for (var i = 1; i <= 3; i++) {
      var optionValue = variant['option' + i];
      if (optionValue != null) {
        if (isPerformanceTreadedLabel(optionValue)) return true;
        if (isPerformanceTreadedBikeModel(optionValue)) return true;
      }
    }

    if (variant.title != null) {
      if (isPerformanceTreadedLabel(variant.title)) return true;
      if (isPerformanceTreadedBikeModel(variant.title)) return true;
    }

    return false;
  }

  function variantMatchesPerformanceTreaded(variant) {
    return variantHasPerformanceTreadedTires(variant);
  }

  function parseAccessoryVariantsFromItem(itemEl) {
    var multiEl = itemEl.querySelector('.rec-accessory-multi');
    var jsonEl = multiEl
      ? multiEl.querySelector('.rec-accessory-variants-json')
      : itemEl.querySelector('.rec-accessory-variants-json');
    if (!jsonEl || !jsonEl.textContent) return [];
    try {
      return JSON.parse(jsonEl.textContent);
    } catch (e) {
      return [];
    }
  }

  function getBarPadReverseSideFromItem(itemEl) {
    if (!itemEl || itemEl.getAttribute('data-product-handle') !== 'antic-bar-pad') return false;

    var toggle = itemEl.querySelector('[data-rec-bar-pad-reverse]');
    if (toggle && toggle.getAttribute('aria-checked') === 'true') return true;

    return itemEl.getAttribute('data-rec-bar-pad-reverse') === 'true';
  }

  function isBarPadSelected(bikeSection) {
    if (!bikeSection || !isConfiguratorStepActive()) return false;

    var itemEl = bikeSection.querySelector(
      '.resource-list__item--complementary[data-product-handle="antic-bar-pad"]'
    );
    if (!itemEl || !accessoryItemShowsLayer(itemEl)) return false;

    return !!getAccessoryVariantFromItem(itemEl);
  }

  function getAccessoryVariantFromItem(itemEl) {
    var renderColor = itemEl.getAttribute('data-rec-render-color');
    var vid = itemEl.getAttribute('data-variant-id');
    if (!vid && !renderColor) return null;

    var variants = parseAccessoryVariantsFromItem(itemEl);
    var variant =
      vid &&
      variants.find(function (v) {
        return String(v.id) === String(vid);
      });

    if (!variant && variants.length) {
      variant = variants[0];
    }

    if (renderColor) {
      var base = variant || { id: vid };
      return Object.assign({}, base, {
        option1: renderColor,
        option2: null,
        option3: null,
      });
    }

    return variant || null;
  }

  function accessoryItemShowsLayer(itemEl) {
    var isMulti = itemEl.classList.contains('resource-list__item--multi-variant');
    return (
      itemEl.classList.contains('is-accessory-selected') ||
      itemEl.classList.contains('is-accessory-preview') ||
      (isMulti && itemEl.classList.contains('is-swatches-open'))
    );
  }

  function isPerformanceTreadedTireSelected(bikeSection) {
    if (!bikeSection) return false;

    var mainVariant = getSelectedMainBikeVariant(bikeSection);
    if (variantHasPerformanceTreadedTires(mainVariant)) return true;

    var picker = bikeSection.querySelector('variant-picker');
    if (picker) {
      var checkedInputs = picker.querySelectorAll('input[type="radio"]:checked');
      for (var i = 0; i < checkedInputs.length; i++) {
        var checkedValue = checkedInputs[i].value;
        if (isPerformanceTreadedLabel(checkedValue)) return true;
        if (isPerformanceTreadedBikeModel(checkedValue)) return true;
      }

      var selects = picker.querySelectorAll('select');
      for (var s = 0; s < selects.length; s++) {
        var selected = selects[s].selectedOptions;
        if (!selected || !selected.length) continue;
        var selectValue = selected[0].value;
        if (isPerformanceTreadedLabel(selectValue)) return true;
        if (isPerformanceTreadedBikeModel(selectValue)) return true;
      }
    }

    var tireLayer = BIKE_OPTION_LAYERS.find(function (layer) {
      return layer.id === 'performance-treaded-tire';
    });
    var tireHandles = (tireLayer && tireLayer.tireHandles) || ['gt-performance-tire'];

    for (var h = 0; h < tireHandles.length; h++) {
      var itemEl = bikeSection.querySelector(
        '.resource-list__item--complementary[data-product-handle="' + tireHandles[h] + '"]'
      );
      if (!itemEl || !accessoryItemShowsLayer(itemEl)) continue;
      var variant = getAccessoryVariantFromItem(itemEl);
      if (variantMatchesPerformanceTreaded(variant)) return true;
    }

    return false;
  }

  function getBikeOptionLayerUrls(bikeSection) {
    var layers = [];

    if (isPerformanceTreadedTireSelected(bikeSection)) {
      BIKE_OPTION_LAYERS.forEach(function (optionLayer) {
        if (optionLayer.id !== 'performance-treaded-tire') return;
        layers.push({ url: cdnUrl(optionLayer.file), z: optionLayer.z });
      });
    }

    return layers;
  }

  function resolveColorSegment(component, optionValue) {
    if (component.match) {
      return component.match({ option1: optionValue, option2: optionValue, option3: optionValue });
    }

    var key = normalizeKey(optionValue);
    if (component.colors && component.colors[key]) return component.colors[key];
    if (COLOR_TOKENS[key]) return COLOR_TOKENS[key];
    return null;
  }

  function variantOptionValues(variant) {
    var values = [];
    if (!variant) return values;
    for (var i = 1; i <= 3; i++) {
      var v = variant['option' + i];
      if (v != null && v !== '') values.push(String(v));
    }
    return values;
  }

  function barPadColorHasReverseSide(colorSegment) {
    return !!(colorSegment && colorSegment !== 'STD_Black');
  }

  function buildFilenames(component, colorSegment, options) {
    options = options || {};
    if (component.file) {
      if (options.barPadSelected && component.padFile) return [component.padFile];
      return [component.file];
    }
    if (!colorSegment || !component.prefix) return [];

    if (component.prefix === 'BarPad' && colorSegment === 'STD_Black') {
      return ['AnticConfig_BarPad_STD_Black_' + VERSION + '.png'];
    }

    if (component.prefix === 'FootPeg') {
      var footPegVersion = colorSegment === 'Black' ? 'V2' : 'V3';
      return ['AnticConfig_FootPeg_' + colorSegment + '_' + footPegVersion + '.png'];
    }

    if (component.layers && component.layers.length) {
      /* Bar Pad filenames: -1 = patterned side (default), -2 = solid side (Reverse Pad on). */
      var suffix;
      if (component.prefix === 'BarPad' && colorSegment === 'HotBlue') {
        /* Antic Logo: blue logo art uses -2; -1 is the reverse side. */
        suffix = options.reverseSide === true ? '-1' : '-2';
      } else {
        suffix = options.reverseSide === true ? '-2' : '-1';
      }
      return [
        'AnticConfig_' +
          component.prefix +
          '_' +
          colorSegment +
          suffix +
          '_' +
          VERSION +
          '.png',
      ];
    }

    if (component.position) {
      var positionVersion = component.version || VERSION;
      return [
        'AnticConfig_' +
          component.prefix +
          '_' +
          colorSegment +
          '_' +
          positionVersion +
          '_' +
          component.position +
          '.png',
      ];
    }

    return [
      'AnticConfig_' + component.prefix + '_' + colorSegment + '_' + VERSION + '.png',
    ];
  }

  function getComponent(productHandle) {
    return handleIndex[normalizeKey(productHandle)] || null;
  }

  /**
   * Resolve render layer objects for one accessory row.
   * @returns {{ url: string, z: number }[]}
   */
  function getLayersForAccessory(productHandle, variant, options) {
    options = options || {};
    var component = getComponent(productHandle);
    if (!component) return [];

    var colorSegment = null;

    if (component.file) {
      return buildFilenames(component, null, options).map(function (file) {
        return { url: cdnUrl(file), z: component.z };
      });
    }

    if (typeof component.match === 'function') {
      colorSegment = component.match(variant);
    } else {
      var optionValues = variantOptionValues(variant);
      for (var i = 0; i < optionValues.length; i++) {
        colorSegment = resolveColorSegment(component, optionValues[i]);
        if (colorSegment) break;
      }
    }

    if (!colorSegment && component.defaultColor) {
      colorSegment = component.defaultColor;
    }

    if (!colorSegment) return [];

    var layerOptions = options;
    if (component.prefix === 'BarPad' && !barPadColorHasReverseSide(colorSegment)) {
      layerOptions = Object.assign({}, options, { reverseSide: false });
    }

    var filenames = buildFilenames(component, colorSegment, layerOptions);
    return filenames.map(function (file) {
      return { url: cdnUrl(file), z: component.z };
    });
  }

  /**
   * Build the full overlay stack for Step 2 (base + selected accessories).
   * Returns empty when not in the customizer step.
   * @param {HTMLElement} bikeSection
   * @returns {{ url: string, z: number }[]}
   */
  function resolveOverlayStack(bikeSection) {
    if (!bikeSection || !isBikeRenderModeActive()) return [];

    var layers = [];
    var seen = {};

    function pushLayer(layer) {
      if (!layer || !layer.url || seen[layer.url]) return;
      seen[layer.url] = true;
      layers.push(layer);
    }

    getBaseLayerUrls().forEach(pushLayer);
    getBikeOptionLayerUrls(bikeSection).forEach(pushLayer);

    if (isConfiguratorStepActive()) {
      var barPadSelected = isBarPadSelected(bikeSection);

      bikeSection
        .querySelectorAll('.resource-list__item--complementary')
        .forEach(function (itemEl) {
          var vid = itemEl.getAttribute('data-variant-id');
          var renderColor = itemEl.getAttribute('data-rec-render-color');
          if (!vid && !renderColor) return;

          if (!accessoryItemShowsLayer(itemEl)) return;

          var handle = itemEl.getAttribute('data-product-handle') || '';
          var variant = getAccessoryVariantFromItem(itemEl);
          if (!variant) return;

          var layerOptions = {
            reverseSide: getBarPadReverseSideFromItem(itemEl),
            barPadSelected: barPadSelected,
          };

          getLayersForAccessory(handle, variant, layerOptions).forEach(pushLayer);
        });
    }

    layers.sort(function (a, b) {
      return a.z - b.z;
    });

    return layers;
  }

  function positionBikeRenderOverlay(bikeSection) {
    var overlay = bikeSection.querySelector('[data-accessory-render-overlay]');
    var mediaWrap = bikeSection.querySelector('.product-information__media');
    if (!overlay || !mediaWrap) return;

    /* Configurator active: CSS owns layout (identical Step 1 / Step 2 mobile framing) */
    if (mediaWrap.classList.contains('antic-configurator-active')) {
      overlay.style.top = '';
      overlay.style.left = '';
      overlay.style.right = '';
      overlay.style.bottom = '';
      overlay.style.width = '';
      overlay.style.height = '';
      overlay.style.transform = '';
      return;
    }

    var slideshow = bikeSection.querySelector('media-gallery slideshow-component');
    if (!slideshow) return;
    var sr = slideshow.getBoundingClientRect();
    var mr = mediaWrap.getBoundingClientRect();
    overlay.style.top = sr.top - mr.top + 'px';
    overlay.style.left = sr.left - mr.left + 'px';
    overlay.style.width = sr.width + 'px';
    overlay.style.height = sr.height + 'px';
    overlay.style.transform = '';
  }

  var lastOverlaySignature = '';

  function stackSignature(stack) {
    return (stack || [])
      .map(function (layer) {
        return (layer.url || '') + ':' + (layer.z != null ? layer.z : '');
      })
      .join('|');
  }

  function isStepTransitioning() {
    var shell = document.querySelector('.bike-pdp');
    return !!(shell && shell.classList.contains('antic-step-transitioning'));
  }

  function syncBikeRenderOverlay() {
    var bikeSection = getBikeSection();
    if (!bikeSection) return;
    var overlay = bikeSection.querySelector('[data-accessory-render-overlay]');
    var layersEl = overlay && overlay.querySelector('[data-accessory-render-layers]');
    if (!overlay || !layersEl) return;

    var renderActive = isBikeRenderModeActive();
    var stack = resolveOverlayStack(bikeSection);
    var signature = stackSignature(stack);

    var mediaWrap = bikeSection.querySelector('.product-information__media');
    if (mediaWrap) {
      mediaWrap.classList.toggle('antic-configurator-active', renderActive && stack.length > 0);
    }

    var show = renderActive && stack.length > 0;
    if (show) {
      if (signature === lastOverlaySignature && !overlay.hasAttribute('hidden')) {
        positionBikeRenderOverlay(bikeSection);
        return;
      }
      applyOverlayLayers(layersEl, stack);
      overlay.removeAttribute('hidden');
      lastOverlaySignature = signature;
    } else if (!isStepTransitioning()) {
      if (overlay.hasAttribute('hidden') && !layersEl.childNodes.length) return;
      layersEl.textContent = '';
      overlay.setAttribute('hidden', 'hidden');
      lastOverlaySignature = '';
    }
    positionBikeRenderOverlay(bikeSection);
  }

  function bindBikeOptionLayerSync() {
    if (typeof document === 'undefined') return;

    function syncOverlay() {
      syncBikeRenderOverlay();
    }

    document.addEventListener('variant:update', function () {
      warmOverlayStack(getBikeSection());
      syncOverlay();
    });
    document.addEventListener('variant:selected', syncOverlay);
    document.addEventListener('variant:change', syncOverlay);
    document.addEventListener(
      'change',
      function (event) {
        if (
          event.target.closest('variant-picker') ||
          event.target.closest('.resource-list__item--complementary')
        ) {
          syncOverlay();
        }
      },
      true
    );
  }

  /** @type {Record<string, { status: string, img: HTMLImageElement, promise?: Promise<unknown> }>} */
  var renderImageCache = {};
  var layerImgPool = {};
  var preconnectAdded = false;

  function getBikeSection() {
    return document.querySelector('.product-information.section[data-product-handle="antic-bike"]');
  }

  function ensureRenderPreconnect() {
    if (preconnectAdded || typeof document === 'undefined') return;
    preconnectAdded = true;
    var origin = CDN_BASE.replace(/^(https?:\/\/[^/]+).*$/, '$1');
    if (!origin || origin === CDN_BASE) return;
    var link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = origin;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  }

  function preloadRenderUrl(url, priority) {
    if (!url) return Promise.resolve();
    var cached = renderImageCache[url];
    if (cached) {
      if (cached.status === 'loaded') return Promise.resolve(cached);
      return cached.promise || Promise.resolve(cached);
    }

    var img = new Image();
    if (priority === 'high' && 'fetchPriority' in img) {
      img.fetchPriority = 'high';
    }

    var entry = { status: 'loading', img: img };
    entry.promise = new Promise(function (resolve) {
      function finish() {
        entry.status = img.naturalWidth > 0 ? 'loaded' : 'error';
        resolve(entry);
      }
      img.onload = finish;
      img.onerror = finish;
      img.src = url;
    });
    renderImageCache[url] = entry;
    return entry.promise;
  }

  function preloadRenderUrls(urls, priority) {
    var unique = [];
    var seen = {};
    (urls || []).forEach(function (url) {
      if (!url || seen[url]) return;
      seen[url] = true;
      unique.push(url);
    });
    return Promise.all(unique.map(function (url) { return preloadRenderUrl(url, priority); }));
  }

  function collectCriticalRenderUrls(bikeSection) {
    var urls = getBaseLayerUrls().map(function (layer) { return layer.url; });
    if (bikeSection) {
      getBikeOptionLayerUrls(bikeSection).forEach(function (layer) {
        urls.push(layer.url);
      });
    }
    ACCESSORY_AUTO_PRESELECT_HANDLES.forEach(function (handle) {
      getLayersForAccessory(handle, null).forEach(function (layer) {
        urls.push(layer.url);
      });
    });
    return urls;
  }

  function warmOverlayStack(bikeSection) {
    bikeSection = bikeSection || getBikeSection();
    if (!bikeSection) return Promise.resolve();

    var urls = collectCriticalRenderUrls(bikeSection);
    if (isBikeRenderModeActive()) {
      resolveOverlayStack(bikeSection).forEach(function (layer) {
        urls.push(layer.url);
      });
    }
    return preloadRenderUrls(urls, 'high');
  }

  function getOrCreateLayerImg(url, index) {
    if (layerImgPool[url]) return layerImgPool[url];

    preloadRenderUrl(url, index <= 1 ? 'high' : 'auto');

    var im = document.createElement('img');
    im.className = 'antic-accessory-render__layer';
    im.alt = 'Configured bike preview';
    im.width = 1400;
    im.height = 1400;
    im.decoding = 'async';
    im.loading = index <= 1 ? 'eager' : 'lazy';
    if (index === 0 && 'fetchPriority' in im) im.fetchPriority = 'high';
    im.setAttribute('data-render-url', url);
    im.src = url;

    if (!im.complete) {
      im.style.opacity = '0';
      im.addEventListener(
        'load',
        function () {
          im.style.opacity = '1';
        },
        { once: true }
      );
    }

    layerImgPool[url] = im;
    return im;
  }

  /**
   * Apply overlay layers while reusing cached img nodes (avoids re-fetch on every sync).
   * @param {HTMLElement} layersEl
   * @param {{ url: string, z?: number }[]} stack
   */
  function applyOverlayLayers(layersEl, stack) {
    if (!layersEl) return;

    var used = {};
    var ordered = [];

    stack.forEach(function (layer, index) {
      if (!layer || !layer.url) return;
      used[layer.url] = true;
      var im = getOrCreateLayerImg(layer.url, index);
      if (layer.z != null) im.style.zIndex = String(layer.z);
      if (im.complete && im.naturalWidth) im.style.opacity = '1';
      ordered.push(im);
    });

    var nextSig = ordered
      .map(function (im) {
        return im.getAttribute('data-render-url') || '';
      })
      .join('|');
    var currentSig = Array.prototype.map
      .call(layersEl.querySelectorAll('.antic-accessory-render__layer'), function (im) {
        return im.getAttribute('data-render-url') || '';
      })
      .join('|');
    if (nextSig && nextSig === currentSig) return;

    Object.keys(layerImgPool).forEach(function (url) {
      if (!used[url] && layerImgPool[url].parentNode === layersEl) {
        layersEl.removeChild(layerImgPool[url]);
      }
    });

    ordered.forEach(function (im) {
      layersEl.appendChild(im);
    });
  }

  function warmAccessoryColorways(productHandle) {
    var colorways = getRenderColorways(productHandle);
    if (!colorways || !colorways.length) return Promise.resolve();
    var urls = [];
    colorways.forEach(function (color) {
      getLayersForAccessory(productHandle, { option1: color }, { reverseSide: false }).forEach(function (layer) {
        urls.push(layer.url);
      });
      getLayersForAccessory(productHandle, { option1: color }, { reverseSide: true }).forEach(function (layer) {
        urls.push(layer.url);
      });
    });
    return preloadRenderUrls(urls, 'low');
  }

  function initBikeRenderPreload() {
    if (typeof document === 'undefined') return;

    ensureRenderPreconnect();

    function boot() {
      var bikeSection = getBikeSection();
      if (!bikeSection) return;

      preloadRenderUrls(collectCriticalRenderUrls(bikeSection), 'high');
      warmOverlayStack(bikeSection);
      syncBikeRenderOverlay();

      document.addEventListener(
        'click',
        function (event) {
          if (
            event.target.closest(
              '.add-to-cart-button, .product-form__submit, button[name="add"], [data-add-to-cart]'
            )
          ) {
            warmOverlayStack(bikeSection);
          }
        },
        true
      );

      document.addEventListener(
        'pointerenter',
        function (event) {
          if (
            event.target.closest(
              '.add-to-cart-button, .product-form__submit, button[name="add"], [data-add-to-cart]'
            )
          ) {
            warmOverlayStack(bikeSection);
          }
        },
        true
      );

      var shell = document.querySelector('.bike-pdp');
      if (shell && typeof MutationObserver !== 'undefined') {
        new MutationObserver(function () {
          if (isStepTransitioning()) return;
          if (isBikeRenderModeActive()) warmOverlayStack(bikeSection);
          syncBikeRenderOverlay();
        }).observe(shell, { attributes: true, attributeFilter: ['class'] });
      }

      var recommendations = document.querySelector('product-recommendations.product-recommendations--complementary');
      if (recommendations && typeof MutationObserver !== 'undefined') {
        var warmTimer = null;
        new MutationObserver(function () {
          if (!isConfiguratorStepActive()) return;
          if (warmTimer) clearTimeout(warmTimer);
          warmTimer = setTimeout(function () {
            warmOverlayStack(bikeSection);
            syncBikeRenderOverlay();
          }, 120);
        }).observe(recommendations, { childList: true, subtree: true });
      }

      window.addEventListener('resize', syncBikeRenderOverlay, { passive: true });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }

    if ('requestIdleCallback' in global) {
      global.requestIdleCallback(function () {
        warmOverlayStack(getBikeSection());
        syncBikeRenderOverlay();
      });
    }
  }

  bindBikeOptionLayerSync();
  initBikeRenderPreload();

  global.anticAccessorySyncBikeRenderOverlay = syncBikeRenderOverlay;

  global.ANTIC_CONFIGURATOR_RENDERS = {
    cdnBase: CDN_BASE,
    version: VERSION,
    baseVersion: BASE_VERSION,
    accessoryHandleOrder: ACCESSORY_HANDLE_ORDER,
    accessoryAutoPreselectHandles: ACCESSORY_AUTO_PRESELECT_HANDLES,
    baseLayers: BASE_LAYERS,
    bikeOptionLayers: BIKE_OPTION_LAYERS,
    components: COMPONENTS,
    renderColorways: RENDER_COLORWAYS,
    getRenderColorways: getRenderColorways,
    getComponent: getComponent,
    getBaseLayerUrls: getBaseLayerUrls,
    getBikeOptionLayerUrls: getBikeOptionLayerUrls,
    getLayersForAccessory: getLayersForAccessory,
    getBarPadReverseSideFromItem: getBarPadReverseSideFromItem,
    barPadColorHasReverseSide: barPadColorHasReverseSide,
    getSelectedMainBikeVariant: getSelectedMainBikeVariant,
    isBikeRenderModeActive: isBikeRenderModeActive,
    isConfiguratorStepActive: isConfiguratorStepActive,
    isPerformanceTreadedTireSelected: isPerformanceTreadedTireSelected,
    variantHasPerformanceTreadedTires: variantHasPerformanceTreadedTires,
    applyOverlayLayers: applyOverlayLayers,
    preloadRenderUrl: preloadRenderUrl,
    preloadRenderUrls: preloadRenderUrls,
    warmOverlayStack: warmOverlayStack,
    warmAccessoryColorways: warmAccessoryColorways,
    resolveOverlayStack: resolveOverlayStack,
    syncBikeRenderOverlay: syncBikeRenderOverlay,
    positionBikeRenderOverlay: positionBikeRenderOverlay,
    normalizeKey: normalizeKey,
  };
})(typeof window !== 'undefined' ? window : globalThis);
